import { Button, Card, Input } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import affiliateServices from "../../../api/services/affiliateServices";
import { useTranslation } from "react-i18next";

type AffiliateCodeInputProps = {
  setPaymentInfo: React.Dispatch<React.SetStateAction<IPaymentInfo>>;
};

const AffiliateCodeInput = ({ setPaymentInfo }: AffiliateCodeInputProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const checkReferralCode = useMemo(
    () =>
      _.debounce(async (value: string) => {
        if (!value) return;

        setLoading(true);
        try {
          const response = await affiliateServices.getAffiliateByRefCode(value);

          if (response.status === 200 && response.data) {
            setStatus({
              success: true,
              message: `${t("affiliate_message.found")}: ${
                response.data.fullname
              }`,
            });
          } else {
            setStatus({
              success: false,
              message: t("affiliate_message.invalid_code"),
            });
          }
        } catch (error) {
          setStatus({
            success: false,
            message: t("affiliate_message.sys_error"),
          });
        }
        setLoading(false);
      }, 700),
    [t]
  );

  useEffect(() => {
    return () => {
      checkReferralCode.cancel();
    };
  }, [checkReferralCode]);

  const handleChange = (e: any) => {
    const value = e.target.value.trim();
    setCode(value);
    setStatus(null);
    checkReferralCode(value);
  };

  return (
    <Card className="p-4 w-full mx-auto mt-6">
      <h2 className="text-xl font-semibold mb-2 text-primary-blue">
        {t("ref_code")}
      </h2>
      <Input
        type="text"
        value={code}
        onChange={handleChange}
        placeholder={t("ref_code")}
        className="w-full p-2 border rounded"
      />
      {loading && (
        <p className="text-blue-500 mt-2 text-sm">{t("verifying")}</p>
      )}
      {status && (
        <p
          className={`mt-2 text-sm ${
            status.success ? "text-green-500" : "text-red-500"
          }`}
        >
          {status.message}
        </p>
      )}

      <div className="w-full flex max-sm:flex-col gap-4 items-center mt-4 justify-center">
        <Button
          type="primary"
          disabled={!status?.success || loading}
          onClick={() =>
            setPaymentInfo((prev) => ({ ...prev, voucher_code: code }))
          }
        >
          {t("cta.apply")}
        </Button>
        <Button
          type="default"
          className="text-primary-blue"
          onClick={() => {
            setCode("");
            setStatus(null);
            setPaymentInfo((prev) => ({ ...prev, voucher_code: "" }));
          }}
        >
          {t("cta.clear")}
        </Button>
      </div>
    </Card>
  );
};

export default AffiliateCodeInput;
